import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { I18n, NgtButtonComponent, NgtInputFieldComponent } from '@corporate/ng-tebis-base-components';
import { ManufacturingOrderService } from '../../../07_feature/manufacturing-order/manufacturing-order.service';
import { ManufacturingOrderModel } from '../../../07_feature/manufacturing-order/manufacturing-order.model';
import { AIForm, AIFormState, shouldFormBeBlocked } from '../../../03_model/ai-chat-message.model';
import { AssistantFormShellComponent } from '../../assistant/assistant-form-shell/assistant-form-shell.component';
import { AssistantService } from '../../../04_service/assistant/assistant.service';
import { OrderCreatedData, OrderCreatedMessageComponent } from '../messages/order-created-message/order-created-message.component';

@Component({
  selector: 'tps-create-order-compact',
  imports: [
    AssistantFormShellComponent,
    NgtInputFieldComponent,
    NgtButtonComponent,
],
  templateUrl: './create-order-compact.component.html',
  styleUrl: './create-order-compact.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CreateOrderCompactComponent implements AIForm {

  i18n = inject(I18n);

  orderService = inject(ManufacturingOrderService);
  assistantService = inject(AssistantService);

  data = input.required<Partial<ManufacturingOrderModel> & AIFormState>();
  update = input.required<(newData: any) => void>();

  orderName = computed(() => this.data().name || '');
  submissionState = computed(() => this.data().submissionState);
  blocked = shouldFormBeBlocked(this.submissionState);
  errorMessage = computed(() => this.data().errorMessage || null);

  constructor() {
    this.i18n.addNamespace('manufacturing-order');
    this.i18n.addNamespace('assistant');
  }

  setOrderName(orderName: string | number | null) {
    if (orderName === null || typeof orderName === 'number') {
      orderName = '';
    }
    this.update()({ name: orderName });
  }

  async createOrder() {
    if (this.blocked()) {
      return;
    }

    this.update()({ submissionState: 'submitting', errorMessage: null });

    try {
      const order = await this.orderService.createOrder({
        name: this.orderName(),
        customerID: this.data().customerID || '',
      });

      await this.assistantService.addMessage({
        type: 'component',
        role: 'user',
        component: OrderCreatedMessageComponent,
        contentForLLM: `Tool result: Created a new manufacturing order with the name "${order.name}".`,
        data: <OrderCreatedData>{
          orderID: order.id,
          sentDate: new Date(),
        },
        requiresFollowUp: false,
      });

      this.update()({ submissionState: 'submitted', errorMessage: null });
    } catch {
      this.update()({ submissionState: 'idle', errorMessage: this.i18n.translate('assistant:formSubmitFailed')() });
    }
  }
}
